/**
 * EF-CREW-INTAKE-WORKSPACE-PHASE-B-V1: Conflict Resolution Dialog
 * 
 * Shows side-by-side comparison of existing vs incoming field values.
 * Admin can choose per field: keep existing, use incoming, or enter custom value.
 */

import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Check,
  X,
  ArrowRight,
  Loader2,
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  CreditCard,
  Shield,
  Pencil,
  History,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import {
  findIntakeMatches,
  getIntakeConflicts,
  resolveIntakeConflicts,
} from '../../lib/api';

// Field icons
const FIELD_ICONS = {
  first_name: User,
  last_name: User,
  email: Mail,
  phone: Phone,
  date_of_birth: Calendar,
  social_security_number: Shield,
  address: MapPin,
  emergency_contact_name: User,
  emergency_contact_phone: Phone,
  bank_name: CreditCard,
  bank_iban: CreditCard,
  bank_bic: CreditCard,
};

// Decision types
const DECISION = {
  KEEP_EXISTING: 'keep_existing',
  USE_INCOMING: 'use_incoming',
  CUSTOM: 'custom',
};

/**
 * MatchResultsCard - Shows potential matches found
 */
export function MatchResultsCard({ matches, onSelectMatch, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Finding matches...</span>
      </div>
    );
  }
  
  if (!matches) return null;
  
  const { match_type, exact_matches, possible_matches, match_reasons } = matches;
  
  if (match_type === 'no_match') {
    return (
      <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10">
        <div className="flex items-center gap-2 text-emerald-400">
          <Check className="w-5 h-5" />
          <span className="font-medium">No Duplicates Found</span>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          No existing crew members match this intake data. Safe to create new.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Exact Matches */}
      {exact_matches.length > 0 && (
        <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/10">
          <div className="flex items-center gap-2 text-red-400 mb-3">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">Exact Match Found ({exact_matches.length})</span>
          </div>
          <div className="space-y-2">
            {exact_matches.map((staff) => (
              <div
                key={staff.id}
                className="flex items-center justify-between p-3 rounded bg-surface border border-border cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => onSelectMatch(staff)}
                data-testid={`match-card-${staff.id}`}
              >
                <div>
                  <p className="font-medium text-sm">
                    {staff.first_name} {staff.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground">{staff.email}</p>
                  <div className="flex gap-1 mt-1">
                    {match_reasons[staff.id]?.map((reason, i) => (
                      <Badge key={i} variant="outline" className="text-[10px] bg-red-500/10 text-red-400 border-red-500/30">
                        {reason}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button variant="ghost" size="sm">
                  Review
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Possible Matches */}
      {possible_matches.length > 0 && (
        <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/10">
          <div className="flex items-center gap-2 text-amber-400 mb-3">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">Possible Match ({possible_matches.length})</span>
          </div>
          <div className="space-y-2">
            {possible_matches.map((staff) => (
              <div
                key={staff.id}
                className="flex items-center justify-between p-3 rounded bg-surface border border-border cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => onSelectMatch(staff)}
                data-testid={`possible-match-${staff.id}`}
              >
                <div>
                  <p className="font-medium text-sm">
                    {staff.first_name} {staff.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground">{staff.email}</p>
                  <div className="flex gap-1 mt-1">
                    {match_reasons[staff.id]?.map((reason, i) => (
                      <Badge key={i} variant="outline" className="text-[10px] bg-amber-500/10 text-amber-400 border-amber-500/30">
                        {reason}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button variant="ghost" size="sm">
                  Review
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ConflictResolutionRow - Single field comparison row
 */
function ConflictResolutionRow({ 
  conflict, 
  decision, 
  customValue,
  onDecisionChange, 
  onCustomValueChange 
}) {
  const Icon = FIELD_ICONS[conflict.field] || User;
  const [editing, setEditing] = useState(false);
  
  const hasConflict = conflict.has_conflict;
  const isNew = conflict.is_new;
  
  return (
    <div 
      className={cn(
        "p-3 rounded-lg border",
        hasConflict && "border-red-500/30 bg-red-500/5",
        isNew && !hasConflict && "border-emerald-500/30 bg-emerald-500/5",
        !hasConflict && !isNew && "border-border bg-surface"
      )}
      data-testid={`conflict-row-${conflict.field}`}
    >
      {/* Field Label */}
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium">{conflict.label}</span>
        {hasConflict && (
          <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-400 border-red-500/30">
            Conflict
          </Badge>
        )}
        {isNew && !hasConflict && (
          <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
            New
          </Badge>
        )}
        {conflict.confidence && (
          <span className="text-[10px] text-muted-foreground ml-auto">
            {Math.round(conflict.confidence * 100)}% confidence
          </span>
        )}
      </div>
      
      {/* Values Comparison */}
      <div className="grid grid-cols-2 gap-3">
        {/* Existing Value */}
        <div
          className={cn(
            "p-2 rounded border cursor-pointer transition-all",
            decision === DECISION.KEEP_EXISTING
              ? "border-primary bg-primary/10"
              : "border-border hover:border-primary/50"
          )}
          onClick={() => onDecisionChange(DECISION.KEEP_EXISTING)}
        >
          <p className="text-[10px] text-muted-foreground mb-1">EXISTING</p>
          <p className={cn(
            "text-sm",
            !conflict.existing_value && "text-muted-foreground italic"
          )}>
            {conflict.existing_value || "Empty"}
          </p>
          {decision === DECISION.KEEP_EXISTING && (
            <Check className="w-4 h-4 text-primary mt-1" />
          )}
        </div>
        
        {/* Incoming Value */}
        <div
          className={cn(
            "p-2 rounded border cursor-pointer transition-all",
            decision === DECISION.USE_INCOMING
              ? "border-primary bg-primary/10"
              : "border-border hover:border-primary/50"
          )}
          onClick={() => onDecisionChange(DECISION.USE_INCOMING)}
        >
          <p className="text-[10px] text-muted-foreground mb-1">INCOMING</p>
          <p className={cn(
            "text-sm",
            !conflict.incoming_value && "text-muted-foreground italic"
          )}>
            {conflict.incoming_value || "Empty"}
          </p>
          {decision === DECISION.USE_INCOMING && (
            <Check className="w-4 h-4 text-primary mt-1" />
          )}
        </div>
      </div>
      
      {/* Custom Value */}
      <div className="mt-2">
        {editing ? (
          <div className="flex gap-2">
            <Input
              value={customValue}
              onChange={(e) => onCustomValueChange(e.target.value)}
              placeholder="Enter custom value..."
              className="h-8 text-sm"
              autoFocus
            />
            <Button 
              size="sm" 
              onClick={() => {
                onDecisionChange(DECISION.CUSTOM);
                setEditing(false);
              }}
            >
              <Check className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        ) : (
          <button
            className={cn(
              "text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors",
              decision === DECISION.CUSTOM && "text-primary"
            )}
            onClick={() => setEditing(true)}
          >
            <Pencil className="w-3 h-3" />
            {decision === DECISION.CUSTOM ? `Custom: ${customValue}` : "Enter custom value"}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * ConflictResolutionDialog - Main dialog for resolving conflicts
 */
export function ConflictResolutionDialog({
  open,
  onOpenChange,
  packetId,
  selectedStaff,
  incomingFields,
  onResolved,
}) {
  const [conflicts, setConflicts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [decisions, setDecisions] = useState({});
  const [customValues, setCustomValues] = useState({});
  
  // Load conflicts when dialog opens
  useEffect(() => {
    if (open && packetId && selectedStaff?.id) {
      loadConflicts();
    }
  }, [open, packetId, selectedStaff]);
  
  const loadConflicts = async () => {
    try {
      setLoading(true);
      const res = await getIntakeConflicts(packetId, selectedStaff.id);
      const data = res.data || res;
      setConflicts(data);
      
      // Initialize decisions - default to keep_existing for all
      const initialDecisions = {};
      const initialCustom = {};
      data.conflicts.forEach((c) => {
        // If there's a conflict or new value, default to incoming
        if (c.has_conflict || c.is_new) {
          initialDecisions[c.field] = DECISION.USE_INCOMING;
        } else {
          initialDecisions[c.field] = DECISION.KEEP_EXISTING;
        }
        initialCustom[c.field] = '';
      });
      setDecisions(initialDecisions);
      setCustomValues(initialCustom);
      
    } catch (error) {
      console.error('Failed to load conflicts:', error);
      toast.error('Failed to load conflict comparison');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDecisionChange = (field, decision) => {
    setDecisions(prev => ({ ...prev, [field]: decision }));
  };
  
  const handleCustomValueChange = (field, value) => {
    setCustomValues(prev => ({ ...prev, [field]: value }));
  };
  
  const handleApplyResolution = async () => {
    try {
      setSaving(true);
      
      // Build field decisions
      const fieldDecisions = conflicts.conflicts.map((c) => ({
        field: c.field,
        action: decisions[c.field],
        incoming_value: c.incoming_value,
        custom_value: customValues[c.field],
      }));
      
      const res = await resolveIntakeConflicts(packetId, selectedStaff.id, fieldDecisions);
      const data = res.data || res;
      
      toast.success(`Updated ${data.fields_updated?.length || 0} fields`, {
        description: `Changes logged (${data.audit_entries_created || 0} audit entries)`,
      });
      
      onResolved?.();
      onOpenChange(false);
      
    } catch (error) {
      console.error('Failed to apply resolution:', error);
      toast.error('Failed to apply changes');
    } finally {
      setSaving(false);
    }
  };
  
  // Count changes
  const changesCount = conflicts?.conflicts?.filter(
    (c) => decisions[c.field] !== DECISION.KEEP_EXISTING
  ).length || 0;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="conflict-resolution-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Resolve Field Conflicts
          </DialogTitle>
          <DialogDescription>
            Compare incoming data with existing crew profile for{' '}
            <strong>{selectedStaff?.first_name} {selectedStaff?.last_name}</strong>.
            Choose which value to keep for each field.
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : conflicts ? (
          <>
            {/* Stats */}
            <div className="flex items-center gap-4 text-sm">
              <span className="text-red-400">
                {conflicts.conflict_count} conflicts
              </span>
              <span className="text-emerald-400">
                {conflicts.new_value_count} new values
              </span>
              <span className="text-muted-foreground ml-auto">
                {changesCount} field(s) will be updated
              </span>
            </div>
            
            {/* Conflict Rows */}
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-3 py-2">
                {conflicts.conflicts.map((conflict) => (
                  <ConflictResolutionRow
                    key={conflict.field}
                    conflict={conflict}
                    decision={decisions[conflict.field]}
                    customValue={customValues[conflict.field]}
                    onDecisionChange={(d) => handleDecisionChange(conflict.field, d)}
                    onCustomValueChange={(v) => handleCustomValueChange(conflict.field, v)}
                  />
                ))}
              </div>
            </ScrollArea>
          </>
        ) : null}
        
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleApplyResolution}
            disabled={saving || !conflicts}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="apply-resolution-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Apply {changesCount > 0 ? `${changesCount} Changes` : 'Resolution'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConflictResolutionDialog;
